import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { AutoComplete, InputNumber, Radio, Select } from 'antd'
import styled from 'styled-components'
import { v4 as uuidv4 } from 'uuid'

import { colours } from '@qflow/theme'

import { AddButton } from 'portal/components/RecordsModules/common'
import { EWC_CODES } from 'portal/lib/constants'

export const AddNewThreshold = ({
  isImperial,
  weightUnit,
  volumeUnit,
  onAddThreshold
}) => {
  const [module, setModule] = useState()
  const [autoCompleteValue, setAutoCompleteValue] = useState<string | null>(
    null
  )
  const [weightValue, setWeightValue] = useState<string | null>(null)
  const [volumeValue, setVolumeValue] = useState<string | null>(null)
  const [options, setOptions] = useState<{ value: string; label: string }[]>([])

  const allProductGroups = useSelector((state: any) => state.productGroups)

  const availableOptions: { value: string; label: string }[] = useMemo(() => {
    if (module === 'delivery') {
      return [
        ...allProductGroups.data.map(pg => ({
          label: pg.name,
          value: pg.productGroupId ? pg.productGroupId : '',
          key: pg.productGroupId ? pg.productGroupId : ''
        }))
      ]
    }
    return EWC_CODES.map(code => ({
      label: `${code.ewcCode} - ${code.description}`,
      value: code.ewcCode,
      key: code.ewcCode
    }))
  }, [allProductGroups, module])

  const handleSearch = value => {
    if (value.length >= 2) {
      // Simulate fetching data based on the input
      const filteredOptions = availableOptions.filter(option =>
        option.value.toLowerCase().includes(value.toLowerCase())
      )
      setOptions(filteredOptions)
    } else {
      setOptions(availableOptions)
    }
  }

  const handleAddThreshold = () => {
    if (
      !module ||
      !autoCompleteValue ||
      (weightValue === null && volumeValue === null)
    ) {
      return
    }

    onAddThreshold({
      thresholdId: uuidv4(),
      module,
      productGroup: module === 'delivery' ? autoCompleteValue : undefined,
      wasteCode: module === 'waste' ? autoCompleteValue : undefined,
      weight: weightValue,
      weightUnit: isImperial ? 'Pound' : 'Tonne',
      volume: volumeValue,
      volumeUnit: isImperial ? 'CubicYard' : 'CubicMeter'
    })

    // Reset the form after adding the threshold
    setModule(undefined)
    setAutoCompleteValue(null)
    setWeightValue(null)
    setVolumeValue(null)
  }

  return (
    <ThresholdContainer>
      <div>
        <ThresholdContainerText>Module</ThresholdContainerText>
        <Radio.Group
          name="module"
          value={module}
          onChange={e => {
            setModule(e.target.value)
            setAutoCompleteValue(null)
          }}
          options={[
            { value: 'delivery', label: 'Delivery' },
            { value: 'waste', label: 'Waste' }
          ]}
        />
      </div>
      <div>
        <ThresholdContainerText>
          Product Group / EWC Code
        </ThresholdContainerText>
        {module === 'delivery' ? (
          <StyledSelect
            id={'productGroupSelect'}
            value={autoCompleteValue}
            allowClear
            showSearch
            placeholder="Select product group"
            options={availableOptions}
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            onChange={(value: any) => setAutoCompleteValue(value)}
          />
        ) : (
          <AutoComplete
            id="ewccode-autocomplete"
            allowClear
            options={options}
            onSearch={handleSearch}
            style={{ width: '100%' }}
            onChange={value => setAutoCompleteValue(value)}
            value={autoCompleteValue}
            placeholder="Enter or search..."
          />
        )}
      </div>
      <WeightVolumeContainer>
        <div>
          <ThresholdContainerText>
            Maximum Weight ({weightUnit})
          </ThresholdContainerText>
          <InputNumber
            style={{ width: '100%' }}
            value={weightValue}
            onChange={value => setWeightValue(value)}
            placeholder="Enter weight"
            disabled={volumeValue ? true : false}
          />
        </div>
        <div>
          <ThresholdContainerText>
            Maximum Volume ({volumeUnit})
          </ThresholdContainerText>
          <InputNumber
            style={{ width: '100%' }}
            value={volumeValue}
            onChange={value => setVolumeValue(value)}
            placeholder="Enter volume"
            disabled={weightValue ? true : false}
          />
        </div>
      </WeightVolumeContainer>
      <ButtonContainer>
        <AddButton
          disabled={
            !module ||
            !autoCompleteValue ||
            (weightValue === null && volumeValue === null)
          }
          onClick={handleAddThreshold}
        >
          Add Threshold
        </AddButton>
      </ButtonContainer>
    </ThresholdContainer>
  )
}

const ThresholdContainer = styled.div`
  background-color: ${colours.WHITE};
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid ${colours.CLOUD_GREY};
  padding: 0.75rem;
  margin-top: 0.25rem;
  width: 442px;
`
const ThresholdContainerText = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.25rem;
`
const StyledSelect = styled(Select)`
  width: 100%;
`
const WeightVolumeContainer = styled.div`
  display: flex;
  gap: 1rem;

  > div {
    flex: 1;
    display: flex;
    flex-direction: column;
  }
`
const ButtonContainer = styled.div`
  display: flex;
  gap: 0.625rem;
  justify-content: flex-end;
`
